import type { ElementType } from 'react'
import type { ButtonProps, ButtonSizes, ButtonVariant, ButtonWeight } from './types'
import { cn } from '@/shared/cn'

const BASE =
  'inline-flex items-center justify-center w-full px-6 py-4 rounded-[8px] text-base transition-all duration-300 cursor-pointer disabled:cursor-not-allowed select-none disabled:translate-y-0 disabled:text-text-secondary'

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-white hover:opacity-90 active:translate-y-0 disabled:bg-[#D0D0D0]',
  secondary:
    'bg-secondary text-[#272727] hover:bg-[#00ff9d] active:translate-y-0 shadow-[0_4px_12px_rgba(0,255,157,0)] hover:shadow-[0_4px_12px_rgba(0,255,157,0.3)]',
  outline:
    'bg-transparent border border-primary text-primary hover:bg-primary hover:text-white active:translate-y-0  disabled:border-[#DFE3E8] disabled:bg-[#D0D0D0]',
}

const SIZES: Record<ButtonSizes, string> = {
  sm: 'px-2 py-1 text-sm rounded-[4px]',
  md: 'px-4 py-2 text-base rounded-[8px]',
  lg: 'px-6 py-3 text-lg rounded-[10px]',
  xl: 'px-8 py-4 text-xl rounded-[12px]',
}

const WEIGHTS: Record<ButtonWeight, string> = {
  normal: 'font-normal',
  bold: 'font-semibold',
}

export function Button<E extends ElementType = 'button'>({
  as,
  variant = 'primary',
  loading = false,
  size = 'md',
  weight = 'normal',
  loadingMessage = 'Entrando…',
  disabled,
  children,
  className,
  ...rest
}: ButtonProps<E>) {
  const Tag = (as ?? 'button') as ElementType

  return (
    <Tag
      className={cn(BASE, VARIANTS[variant], SIZES[size], WEIGHTS[weight], className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <svg
            className="animate-spin h-5 w-5 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          {loadingMessage}
        </span>
      ) : (
        children
      )}
    </Tag>
  )
}
